import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Tag, Checkbox, Empty, Input } from '@drip/drip-design';
import { CloseOutlined, CheckOutlined, RightOutlined, DownOutlined } from '@drip/drip-design-icons';
import { uniqBy, differenceBy, cloneDeep, isEqual, forEachRight } from 'lodash';
import Dropdown from 'rc-dropdown';
import { CellMeasurerCache, AutoSizer, List, CellMeasurer } from 'react-virtualized';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter);
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var UseCascader = function UseCascader() {
  var ref = useRef();

  var getParentMerge = function getParentMerge() {
    return ref.current ? ref.current.merge() : [];
  };

  var reset = function reset() {
    return ref.current ? ref.current.reset() : [];
  };

  return [{
    ref: ref,
    getParentMerge: getParentMerge,
    reset: reset
  }];
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$1 = "@font-face{font-family:anticon;src:url(//at.alicdn.com/t/font_1434092639_4910953.eot);src:url(//at.alicdn.com/t/font_1434092639_4910953.eot#iefix) format(\"embedded-opentype\"),url(//at.alicdn.com/t/font_1434092639_4910953.woff) format(\"woff\"),url(//at.alicdn.com/t/font_1434092639_4910953.ttf) format(\"truetype\"),url(//at.alicdn.com/t/font_1434092639_4910953.svg#iconfont) format(\"svg\")}.rc-dropdown{position:absolute;left:-9999px;top:-9999px;z-index:1070;display:block;font-family:Helvetica Neue,Helvetica,Arial,sans-serif;font-size:12px;font-weight:400;line-height:1.5}.rc-dropdown-hidden{display:none}.rc-dropdown-menu{outline:none;position:relative;list-style-type:none;padding:0;margin:2px 0;text-align:left;background-color:#fff;border-radius:3px;box-shadow:0 1px 5px #ccc;background-clip:padding-box;border:1px solid #ccc}.rc-dropdown-menu>li{margin:0;padding:0}.rc-dropdown-menu:before{content:\"\";position:absolute;top:-4px;left:0;width:100%;height:4px;background:#fff;background:hsla(0,0%,100%,.01)}.rc-dropdown-menu>.rc-dropdown-menu-item{position:relative;display:block;padding:7px 10px;clear:both;font-size:12px;font-weight:400;color:#666;white-space:nowrap}.rc-dropdown-menu>.rc-dropdown-menu-item-active,.rc-dropdown-menu>.rc-dropdown-menu-item-selected,.rc-dropdown-menu>.rc-dropdown-menu-item:hover{background-color:#ebfaff}.rc-dropdown-menu>.rc-dropdown-menu-item-selected{position:relative}.rc-dropdown-menu>.rc-dropdown-menu-item-selected:after{content:\"\\e613\";font-family:anticon;font-weight:700;position:absolute;top:6px;right:16px;color:#3cb8f0}.rc-dropdown-menu>.rc-dropdown-menu-item-disabled{color:#ccc;cursor:not-allowed;pointer-events:none}.rc-dropdown-menu>.rc-dropdown-menu-item-disabled:hover{color:#ccc;background-color:#fff;cursor:not-allowed}.rc-dropdown-menu>.rc-dropdown-menu-item:last-child{border-bottom-left-radius:3px;border-bottom-right-radius:3px}.rc-dropdown-menu>.rc-dropdown-menu-item:first-child{border-top-left-radius:3px;border-top-right-radius:3px}.rc-dropdown-menu>.rc-dropdown-menu-item-divider{height:1px;margin:1px 0;overflow:hidden;background-color:#e5e5e5;line-height:0}.rc-dropdown-slide-up-appear,.rc-dropdown-slide-up-enter{-webkit-animation-duration:.3s;animation-duration:.3s;-webkit-animation-fill-mode:both;animation-fill-mode:both;-webkit-transform-origin:0 0;transform-origin:0 0;display:block!important;opacity:0;-webkit-animation-timing-function:cubic-bezier(.08,.82,.17,1);animation-timing-function:cubic-bezier(.08,.82,.17,1);-webkit-animation-play-state:paused;animation-play-state:paused}.rc-dropdown-slide-up-leave{-webkit-animation-duration:.3s;animation-duration:.3s;-webkit-animation-fill-mode:both;animation-fill-mode:both;-webkit-transform-origin:0 0;transform-origin:0 0;display:block!important;opacity:1;-webkit-animation-timing-function:cubic-bezier(.6,.04,.98,.34);animation-timing-function:cubic-bezier(.6,.04,.98,.34);-webkit-animation-play-state:paused;animation-play-state:paused}.rc-dropdown-slide-up-appear.rc-dropdown-slide-up-appear-active.rc-dropdown-placement-bottomCenter,.rc-dropdown-slide-up-appear.rc-dropdown-slide-up-appear-active.rc-dropdown-placement-bottomLeft,.rc-dropdown-slide-up-appear.rc-dropdown-slide-up-appear-active.rc-dropdown-placement-bottomRight,.rc-dropdown-slide-up-enter.rc-dropdown-slide-up-enter-active.rc-dropdown-placement-bottomCenter,.rc-dropdown-slide-up-enter.rc-dropdown-slide-up-enter-active.rc-dropdown-placement-bottomLeft,.rc-dropdown-slide-up-enter.rc-dropdown-slide-up-enter-active.rc-dropdown-placement-bottomRight{-webkit-animation-name:rcDropdownSlideUpIn;animation-name:rcDropdownSlideUpIn;-webkit-animation-play-state:running;animation-play-state:running}.rc-dropdown-slide-up-appear.rc-dropdown-slide-up-appear-active.rc-dropdown-placement-topCenter,.rc-dropdown-slide-up-appear.rc-dropdown-slide-up-appear-active.rc-dropdown-placement-topLeft,.rc-dropdown-slide-up-appear.rc-dropdown-slide-up-appear-active.rc-dropdown-placement-topRight,.rc-dropdown-slide-up-enter.rc-dropdown-slide-up-enter-active.rc-dropdown-placement-topCenter,.rc-dropdown-slide-up-enter.rc-dropdown-slide-up-enter-active.rc-dropdown-placement-topLeft,.rc-dropdown-slide-up-enter.rc-dropdown-slide-up-enter-active.rc-dropdown-placement-topRight{-webkit-animation-name:rcDropdownSlideDownIn;animation-name:rcDropdownSlideDownIn;-webkit-animation-play-state:running;animation-play-state:running}.rc-dropdown-slide-up-leave.rc-dropdown-slide-up-leave-active.rc-dropdown-placement-bottomCenter,.rc-dropdown-slide-up-leave.rc-dropdown-slide-up-leave-active.rc-dropdown-placement-bottomLeft,.rc-dropdown-slide-up-leave.rc-dropdown-slide-up-leave-active.rc-dropdown-placement-bottomRight{-webkit-animation-name:rcDropdownSlideUpOut;animation-name:rcDropdownSlideUpOut;-webkit-animation-play-state:running;animation-play-state:running}.rc-dropdown-slide-up-leave.rc-dropdown-slide-up-leave-active.rc-dropdown-placement-topCenter,.rc-dropdown-slide-up-leave.rc-dropdown-slide-up-leave-active.rc-dropdown-placement-topLeft,.rc-dropdown-slide-up-leave.rc-dropdown-slide-up-leave-active.rc-dropdown-placement-topRight{-webkit-animation-name:rcDropdownSlideDownOut;animation-name:rcDropdownSlideDownOut;-webkit-animation-play-state:running;animation-play-state:running}@-webkit-keyframes rcDropdownSlideUpIn{0%{opacity:0;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(0);transform:scaleY(0)}to{opacity:1;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(1);transform:scaleY(1)}}@keyframes rcDropdownSlideUpIn{0%{opacity:0;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(0);transform:scaleY(0)}to{opacity:1;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(1);transform:scaleY(1)}}@-webkit-keyframes rcDropdownSlideUpOut{0%{opacity:1;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(1);transform:scaleY(1)}to{opacity:0;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(0);transform:scaleY(0)}}@keyframes rcDropdownSlideUpOut{0%{opacity:1;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(1);transform:scaleY(1)}to{opacity:0;-webkit-transform-origin:0 0;transform-origin:0 0;-webkit-transform:scaleY(0);transform:scaleY(0)}}@-webkit-keyframes rcDropdownSlideDownIn{0%{opacity:0;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(0);transform:scaleY(0)}to{opacity:1;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(1);transform:scaleY(1)}}@keyframes rcDropdownSlideDownIn{0%{opacity:0;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(0);transform:scaleY(0)}to{opacity:1;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(1);transform:scaleY(1)}}@-webkit-keyframes rcDropdownSlideDownOut{0%{opacity:1;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(1);transform:scaleY(1)}to{opacity:0;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(0);transform:scaleY(0)}}@keyframes rcDropdownSlideDownOut{0%{opacity:1;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(1);transform:scaleY(1)}to{opacity:0;-webkit-transform-origin:0 100%;transform-origin:0 100%;-webkit-transform:scaleY(0);transform:scaleY(0)}}.rc-dropdown-arrow{position:absolute;box-shadow:0 1px 5px #ccc;border:4px solid transparent;-webkit-transform:rotate(45deg);transform:rotate(45deg)}.rc-dropdown-show-arrow.rc-dropdown-placement-top,.rc-dropdown-show-arrow.rc-dropdown-placement-topLeft,.rc-dropdown-show-arrow.rc-dropdown-placement-topRight{padding-bottom:6px}.rc-dropdown-show-arrow.rc-dropdown-placement-bottom,.rc-dropdown-show-arrow.rc-dropdown-placement-bottomLeft,.rc-dropdown-show-arrow.rc-dropdown-placement-bottomRight{padding-top:6px}.rc-dropdown-placement-top .rc-dropdown-arrow,.rc-dropdown-placement-topLeft .rc-dropdown-arrow,.rc-dropdown-placement-topRight .rc-dropdown-arrow{bottom:4px;border-top-color:#fff}.rc-dropdown-placement-top .rc-dropdown-arrow{left:50%}.rc-dropdown-placement-topLeft .rc-dropdown-arrow{left:15%}.rc-dropdown-placement-topRight .rc-dropdown-arrow{right:15%}.rc-dropdown-placement-bottom .rc-dropdown-arrow,.rc-dropdown-placement-bottomLeft .rc-dropdown-arrow,.rc-dropdown-placement-bottomRight .rc-dropdown-arrow{top:4px;border-bottom-color:#fff}.rc-dropdown-placement-bottom .rc-dropdown-arrow{left:50%}.rc-dropdown-placement-bottomLeft .rc-dropdown-arrow{left:15%}.rc-dropdown-placement-bottomRight .rc-dropdown-arrow{right:15%}";
styleInject(css_248z$1);

var CascaderTag = function CascaderTag(props) {
  var _props$selectVal = props.selectVal,
      selectVal = _props$selectVal === void 0 ? [] : _props$selectVal,
      onClose = props.onClose,
      disabled = props.disabled;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: '100%'
    }
  }, selectVal.length > 0 && /*#__PURE__*/React.createElement(Tag, null, /*#__PURE__*/React.createElement("div", {
    className: "caseader-tag"
  }, "\u5DF2\u9009", selectVal.length, "\u9879", !disabled && /*#__PURE__*/React.createElement("span", {
    className: "caseader-close-icon",
    onClick: function onClick() {
      return onClose();
    }
  }, /*#__PURE__*/React.createElement(CloseOutlined, {
    style: {
      color: '#fff',
      fontSize: 12
    }
  })))));
};

var CascaderTag$1 = /*#__PURE__*/React.memo(CascaderTag);

var css_248z = ".ReactVirtualized__Table__headerRow{font-weight:700;text-transform:uppercase}.ReactVirtualized__Table__headerRow,.ReactVirtualized__Table__row{display:-webkit-flex;display:flex;-webkit-flex-direction:row;flex-direction:row;-webkit-align-items:center;align-items:center}.ReactVirtualized__Table__headerTruncatedText{display:inline-block;max-width:100%;white-space:nowrap;text-overflow:ellipsis;overflow:hidden}.ReactVirtualized__Table__headerColumn,.ReactVirtualized__Table__rowColumn{margin-right:10px;min-width:0}.ReactVirtualized__Table__rowColumn{text-overflow:ellipsis;white-space:nowrap}.ReactVirtualized__Table__headerColumn:first-of-type,.ReactVirtualized__Table__rowColumn:first-of-type{margin-left:10px}.ReactVirtualized__Table__sortableHeaderColumn{cursor:pointer}.ReactVirtualized__Table__sortableHeaderIconContainer{display:-webkit-flex;display:flex;-webkit-align-items:center;align-items:center}.ReactVirtualized__Table__sortableHeaderIcon{-webkit-flex:0 0 24px;flex:0 0 24px;height:1em;width:1em;fill:currentColor}";
styleInject(css_248z);

var createTree = function createTree(show, fieldNames, treeData, parentIndex, parentValue, level) {
  if (Array.isArray(treeData)) {
    treeData.forEach(function (item, index) {
      item.index = "".concat(parentIndex ? parentIndex + '-' : '').concat(index);
      item.label = item[fieldNames.label];
      item.value = item[fieldNames.value];
      item.allParentValueIndex = "".concat(parentValue ? parentValue + '-' : '').concat(item.value);
      item.isLeaf = false;
      item.disabled = item.disabled || show;
      item.isOpen = false;
      item.checked = false;
      item.indeterminate = false;
      item.level = level ? level : 0;
      item.children = item.children ? item.children : [];

      if (item.children && item.children.length) {
        item.children = createTree(show, fieldNames, item.children, item.index, item.allParentValueIndex, item.level + 1);
      } else {
        item.isLeaf = true;
      }
    });
  }

  return treeData;
};
var getTreeNode = function getTreeNode(treeData, position) {
  var positionIndex = position.split('-');
  var treeNode = treeData;
  var node = {
    label: '',
    allParentValueIndex: '',
    value: '',
    index: '0',
    level: 0,
    indeterminate: false,
    checked: false,
    children: []
  };
  positionIndex.forEach(function (pos, index) {
    if (index === positionIndex.length - 1) {
      node = treeNode[+pos];
    } else {
      treeNode = treeNode[+pos].children || [];
    }
  });
  return node;
};
var getCheckCurrentStatus = function getCheckCurrentStatus(current) {
  return current.every(function (item) {
    return item.checked;
  }) ? 'checkAll' : current.some(function (item) {
    return item.checked || item.indeterminate;
  }) ? 'indeterminate' : 'close';
}; // 目前只支持子级值遍历
// 匹配状态选择

var getCheckStatus = function getCheckStatus(treeData, value) {
  var data = treeData;
  var selectList = [];
  var valuesFake = value.map(function (item) {
    return {
      id: item,
      finded: false
    };
  });

  if (value && value.length) {
    for (var index = 0; index < valuesFake.length; index++) {
      var selectVal = valuesFake[index];
      data = checkChildTree(data, selectVal, selectList);
    }
  }

  return {
    treeData: data,
    selectList: selectList
  };
};
var checkChildTree = function checkChildTree(treeData, value, selectList) {
  if (value.finded) return treeData;

  for (var index = 0; index < treeData.length; index++) {
    if (value.finded) return treeData;
    var tree = treeData[index];

    if (value.id === tree.value) {
      tree.checked = true;
      value.finded = true;
      selectList.push(tree);
      break;
    }

    if (tree.children && tree.children.length) {
      tree.children = checkChildTree(tree.children, value, selectList);

      switch (getCheckCurrentStatus(tree.children)) {
        case 'checkAll':
          tree.checked = true;
          tree.indeterminate = false;
          break;

        case 'indeterminate':
          tree.checked = false;
          tree.indeterminate = true;
          break;

        case 'close':
          tree.checked = false;
          tree.indeterminate = false;
          break;
      }
    }
  }

  return treeData;
}; // 查询节点信息

var searchNodeMsg = function searchNodeMsg(treeData, val) {
  var searchVal = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : [];
  var result = searchVal;
  if (!val) return result;

  for (var index = 0; index < treeData.length; index++) {
    var element = treeData[index];
    var name = element.label.toLocaleLowerCase();

    if (name.indexOf(val.toLocaleLowerCase()) >= 0) {
      getAllLeafNode(element, result);
    } else {
      if (element.children && element.children) {
        searchNodeMsg(element.children, val, result);
      }
    }
  }

  return result;
}; // 获取所有叶子节点

var getAllLeafNode = function getAllLeafNode(node, result) {
  if (node.children && node.children.length) {
    node.children.forEach(function (item) {
      getAllLeafNode(item, result);
    });
  } else {
    result.push(node);
  }
}; // 父节点如果勾选则找到所有子节点解析

var getParentToChildVal = function getParentToChildVal(treeData, values) {
  var data = treeData;
  var selectList = [];
  var valuesFake = values.map(function (item) {
    return {
      id: item,
      finded: false
    };
  });

  if (values && values.length) {
    valuesFake.forEach(function (i) {
      getChild(data, i, selectList, false);
    });
  }

  return selectList;
};
var getChild = function getChild(treeData, val, selectList) {
  var pushAll = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
  if (val.finded) return;

  for (var index = 0; index < treeData.length; index++) {
    if (val.finded) break;
    var tree = treeData[index];

    if (val.id === tree.value || pushAll) {
      if (tree.children && !tree.children.length) {
        selectList.push(tree);

        if (!pushAll) {
          val.finded = true;
          break;
        }
      } else {
        getChild(tree.children ? tree.children : [], val, selectList, true);
      }
    }

    if (tree.children && tree.children.length) {
      getChild(tree.children, val, selectList);
    }
  }
}; // 合并子节点的值

var mergeChildVals = function mergeChildVals(treeData, selectList) {
  if (treeData && treeData.length) {
    for (var index = 0; index < treeData.length; index++) {
      var tree = treeData[index];

      if (tree.checked) {
        selectList.push(tree);
      } else if (tree.children && tree.children.length) {
        mergeChildVals(tree.children, selectList);
      }
    }
  }

  return selectList;
};

var SeacrhPanel = function SeacrhPanel(props) {
  var inputRef = props.inputRef,
      searchVal = props.searchVal,
      searchResultVal = props.searchResultVal,
      menu = props.menu,
      getSearchCheckVal = props.getSearchCheckVal,
      selectVal = props.selectVal;

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      list = _useState2[0],
      setList = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      containerWidth = _useState4[0],
      setWidth = _useState4[1];

  var _useState5 = useState(new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: 30
  })),
      _useState6 = _slicedToArray(_useState5, 1),
      cache = _useState6[0];

  useEffect(function () {
    if (inputRef) {
      setWidth(inputRef.current.offsetWidth);
    }
  }, [inputRef]);
  useEffect(function () {
    if (searchResultVal) {
      cache.clearAll();
      setList(_toConsumableArray(searchResultVal));
    }
  }, [searchResultVal]);

  var renderListItem = function renderListItem(item) {
    var column = item.index.split('-'); // [0, 1, 2]

    var showRowlist = [];
    column.forEach(function (ele, i) {
      showRowlist.push(getTreeNode(menu, column.slice(0, i + 1).join('-')).label);
    });
    return /*#__PURE__*/React.createElement("div", null, showRowlist.map(function (label, i) {
      return /*#__PURE__*/React.createElement("div", {
        key: i,
        style: {
          display: 'inline-block'
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          display: 'inline-block'
        },
        dangerouslySetInnerHTML: {
          __html: renderLabel(label, searchVal)
        }
      }), i !== showRowlist.length - 1 ? ' / ' : '');
    }));
  };

  var renderLabel = function renderLabel(label, str) {
    var reg = new RegExp(str, 'gi');
    return label.replace(reg, function (word) {
      return "<em class='caseader-search-highlight' style=\"display: inline-block; font-style: normal;\">".concat(word, "</em>");
    });
  };

  var changeCheck = function changeCheck(item) {
    item.checked = !item.checked;
    var result = [];

    if (item.checked) {
      result = uniqBy([].concat(_toConsumableArray(selectVal), [item]), 'value');
    } else [result = differenceBy(selectVal, [item], 'value')];

    getSearchCheckVal(result);
  };

  var cellRenderer = function cellRenderer(_ref) {
    var index = _ref.index,
        key = _ref.key,
        parent = _ref.parent,
        style = _ref.style;
    return /*#__PURE__*/React.createElement(CellMeasurer, {
      cache: cache,
      columnIndex: 0,
      key: key,
      parent: parent,
      rowIndex: index
    }, function (_ref2) {
      _ref2.registerChild;
      return /*#__PURE__*/React.createElement("div", {
        key: key,
        style: style,
        className: "caseader-search-text",
        onClick: function onClick() {
          return changeCheck(list[index]);
        }
      }, renderListItem(list[index]), list[index].checked && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(CheckOutlined, {
        className: "caseader-search-highlight"
      })));
    });
  };

  return /*#__PURE__*/React.createElement("div", {
    style: {
      width: containerWidth - 10,
      height: 300
    }
  }, /*#__PURE__*/React.createElement(AutoSizer, {
    disableHeight: true
  }, function (_ref3) {
    _ref3.height;
        var width = _ref3.width;
    return /*#__PURE__*/React.createElement(List, {
      style: {
        outline: 'none'
      },
      height: 300,
      rowCount: list.length,
      rowHeight: cache.rowHeight,
      deferredMeasurementCache: cache,
      rowRenderer: cellRenderer,
      width: width
    });
  }));
};

var MultipleCascader = function MultipleCascader(props, ref) {
  var style = props.style,
      placeholder = props.placeholder,
      _props$options = props.options,
      options = _props$options === void 0 ? [] : _props$options,
      _props$cascader = props.cascader,
      cascader = _props$cascader === void 0 ? {
    ref: useRef()
  } : _props$cascader,
      _props$value = props.value,
      value = _props$value === void 0 ? [] : _props$value,
      getPopupContainer = props.getPopupContainer,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$show = props.show,
      show = _props$show === void 0 ? false : _props$show,
      _props$emptyProps = props.emptyProps,
      emptyProps = _props$emptyProps === void 0 ? {
    description: '暂无数据'
  } : _props$emptyProps,
      _props$onPopupVisible = props.onPopupVisibleChange,
      onPopupVisibleChange = _props$onPopupVisible === void 0 ? function () {} : _props$onPopupVisible,
      _props$onChange = props.onChange,
      onChange = _props$onChange === void 0 ? function () {} : _props$onChange,
      _props$fieldNames = props.fieldNames,
      fieldNames = _props$fieldNames === void 0 ? {
    label: 'label',
    value: 'value'
  } : _props$fieldNames;

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      timer = _useState2[0],
      setTimer = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      visible = _useState4[0],
      setVisible = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isSearch = _useState6[0],
      setIsSearch = _useState6[1];

  var _useState7 = useState([]),
      _useState8 = _slicedToArray(_useState7, 2),
      searchResultVal = _useState8[0],
      setSearchResultVal = _useState8[1];

  var _useState9 = useState(''),
      _useState10 = _slicedToArray(_useState9, 2),
      searchVal = _useState10[0],
      setSearchVal = _useState10[1];

  var inputRef = useRef();

  var _useState11 = useState({}),
      _useState12 = _slicedToArray(_useState11, 2),
      originTree = _useState12[0],
      setOriginTree = _useState12[1];

  var _useState13 = useState([]),
      _useState14 = _slicedToArray(_useState13, 2),
      selectVal = _useState14[0],
      setSelectVal = _useState14[1];

  var _useState15 = useState({}),
      _useState16 = _slicedToArray(_useState15, 2),
      menu = _useState16[0],
      setMenu = _useState16[1];

  useEffect(function () {
    if (options && options.length) {
      var treeData = createTree(show, fieldNames, cloneDeep(options));
      setOriginTree(cloneDeep({
        '0': treeData
      }));

      if (value && value.length) {
        initialValue();
      } else {
        setMenu({
          '0': treeData
        });
      }
    }
  }, [options]);
  useEffect(function () {
    if (value && !isEqual(value, selectVal.map(function (i) {
      return i.value;
    }))) {
      initialValue();
    }
  }, [value]);

  var initialValue = function initialValue() {
    var treeData = createTree(show, fieldNames, cloneDeep(options));
    var dealData = getCheckStatus(treeData, changeValeToChild(treeData, value));
    setMenu({
      '0': dealData.treeData
    });
    setSelectVal(dealData.selectList);
  };

  var changeValeToChild = function changeValeToChild(list, value) {
    return Array.from(new Set(getParentToChildVal(list, value).map(function (i) {
      return i.value;
    })));
  };

  var closeOpen = function closeOpen(list) {
    list.forEach(function (i) {
      return i.isOpen = false;
    });
  };

  var toogle = function toogle(item, currentList) {
    if (item.isLeaf) return;
    closeOpen(currentList);
    closeOpen(item.children ? item.children : []);
    item.isOpen = true;
    var m = {};
    m["".concat(item.level + 1)] = item.children;
    var keys = Object.keys(menu).map(function (i) {
      return +i;
    });
    keys.forEach(function (i) {
      // 展示第0列当前点击列的下一列
      if (i > item.level + 1) {
        delete menu[i + ''];
      }
    });
    setMenu(_objectSpread2(_objectSpread2({}, menu), m));
  };

  var check = function check(item, e, currentList) {
    e.stopPropagation();
    item.checked = !item.checked;
    item.indeterminate = false;
    var child = item.children;

    if (child && !item.isLeaf) {
      changeCheckboxStatus(child, item.checked);
    } // setMenu({ ...menu })


    toogle(item, currentList); // level从0开始如果是0表示没有父节点

    if (item.level) {
      changeParentStatus(item);
    }

    onSelect(item);
  }; // 提供给父组件调用


  useImperativeHandle(cascader.ref, function () {
    return {
      merge: function merge() {
        var dealData = getCheckStatus(cloneDeep(originTree[0]), selectVal.map(function (i) {
          return i.value;
        }));
        return mergeChildVals(dealData.treeData, []);
      },
      reset: function reset() {
        return _onClose();
      }
    };
  }); // 处理选中值逻辑(checkAll状态需要合并为父value)

  var onSelect = function onSelect(item) {
    if (item.isLeaf) {
      onSelectLeaf(item);
    } else {
      onSelectAllChild(item);
    }
  };

  var pushOnChange = function pushOnChange(val) {
    onChange(val.map(function (i) {
      return i.value;
    }), {
      treeData: menu['0'],
      selectValTreeNode: val
    });
  };

  var onSelectAllChild = function onSelectAllChild(item) {
    var newSelect = [];

    if (item.checked) {
      selectVal.push.apply(selectVal, _toConsumableArray(getAllChildVal(item)));

      var uniqValue = _toConsumableArray(uniqBy(selectVal, 'value'));

      setSelectVal(uniqValue);
      pushOnChange(uniqValue);
    } else {
      newSelect = differenceBy(selectVal, getAllChildVal(item), 'value');
      setSelectVal(_toConsumableArray(newSelect));
      pushOnChange(newSelect);
    }
  };

  var getAllChildVal = function getAllChildVal(item) {
    var allChild = [];

    if (item.children && item.children.length) {
      item.children.forEach(function (i) {
        allChild.push.apply(allChild, _toConsumableArray(getAllChildVal(i)));
      });
    } else {
      allChild.push(item);
    }

    return allChild;
  };

  var onSelectLeaf = function onSelectLeaf(item) {
    if (item.checked) {
      selectVal.push(item);
      setSelectVal(_toConsumableArray(selectVal));
      pushOnChange(selectVal);
    } else {
      var newSelect = [];
      newSelect = differenceBy(selectVal, [item], 'value');
      setSelectVal(_toConsumableArray(newSelect));
      pushOnChange(newSelect);
    }
  }; // 处理上级半选中状态


  var changeParentStatus = function changeParentStatus(item) {
    var listNums = item.index ? item.index.split('-') : [];
    forEachRight(listNums, function (_val, i) {
      if (i > 0) {
        var parentPos = listNums[i - 1];
        var parentIndex = i - 1;

        switch (getCheckCurrentStatus(menu[i])) {
          case 'checkAll':
            menu["".concat(parentIndex)][+parentPos].checked = true;
            menu["".concat(parentIndex)][+parentPos].indeterminate = false;
            break;

          case 'indeterminate':
            menu["".concat(parentIndex)][+parentPos].checked = false;
            menu["".concat(parentIndex)][+parentPos].indeterminate = true;
            break;

          case 'close':
            menu["".concat(parentIndex)][+parentPos].checked = false;
            menu["".concat(parentIndex)][+parentPos].indeterminate = false;
            break;
        }
      }
    });
  };

  var changeCheckboxStatus = function changeCheckboxStatus(menuList) {
    var status = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    menuList.forEach(function (item) {
      item.checked = status;
      item.indeterminate = false;
      if (item.children) changeCheckboxStatus(item.children, status);
    });
  };

  var _onClose = function onClose() {
    setSelectVal([]);
    pushOnChange([]);
    setMenu(cloneDeep(originTree));
  };

  var renderCheckbox = function renderCheckbox(text, item, ele) {
    return /*#__PURE__*/React.createElement("div", {
      onClick: function onClick() {
        return toogle(item, ele);
      },
      className: show && !item.checked && !item.indeterminate ? 'show-label' : ''
    }, /*#__PURE__*/React.createElement(Checkbox, {
      disabled: item.disabled || false,
      checked: item.checked,
      indeterminate: item.indeterminate,
      onClick: function onClick(e) {
        return check(item, e, ele);
      }
    }), /*#__PURE__*/React.createElement("span", {
      className: 'check-label ' + (item.isOpen ? 'color-label-expanded' : '')
    }, text), !item.isLeaf && /*#__PURE__*/React.createElement(RightOutlined, {
      className: 'arrow ' + (item.isOpen ? 'color-label-expanded' : ''),
      style: {
        color: !item.isOpen && show && !item.checked && !item.indeterminate ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.65)'
      }
    }));
  };

  var renderDropdown = function renderDropdown() {
    return /*#__PURE__*/React.createElement("div", {
      className: "cascader-top-container"
    }, /*#__PURE__*/React.createElement("div", {
      className: "cascader-menu"
    }, Object.values(menu).map(function (ele, index) {
      return /*#__PURE__*/React.createElement("ul", {
        className: "cascader-list",
        key: index
      }, ele.map(function (item) {
        return /*#__PURE__*/React.createElement("li", {
          key: item.value
        }, renderCheckbox(item.label, item, ele));
      }));
    })));
  };

  var getSearchCheckVal = function getSearchCheckVal(val) {
    setSelectVal(_toConsumableArray(val));
    pushOnChange(val);
  };

  var renderSearch = function renderSearch() {
    if (searchResultVal.length) {
      return /*#__PURE__*/React.createElement("div", {
        style: {
          padding: '4px 0 4px 10px'
        },
        className: "cascader-top-container"
      }, /*#__PURE__*/React.createElement(SeacrhPanel, {
        inputRef: inputRef,
        searchResultVal: searchResultVal,
        menu: menu['0'],
        searchVal: searchVal,
        selectVal: selectVal,
        getSearchCheckVal: getSearchCheckVal
      }));
    } else {
      return /*#__PURE__*/React.createElement("div", {
        className: "cascader-top-container",
        style: {
          width: inputRef.current ? inputRef.current.offsetWidth : 300,
          height: 77
        }
      }, /*#__PURE__*/React.createElement(Empty, {
        description: "\u6682\u65E0\u641C\u7D22\u7ED3\u679C",
        image: Empty.PRESENTED_IMAGE_SIMPLE,
        imageStyle: {
          width: 62,
          height: 35,
          transform: 'scale(.7)'
        }
      }));
    }
  };

  var debounce = function debounce(fn, wait) {
    return function (input) {
      input.persist();
      if (timer !== null) clearTimeout(timer);
      setTimer(setTimeout(fn, wait, input));
    };
  }; // 搜索


  var search = function search(e) {
    var val = e.target.value;
    setIsSearch(true);
    setSearchVal(val);
    setVisible(true);
    debounce(function () {
      setSearchResultVal(searchNodeMsg(menu['0'], val));
    }, 700)(e);
  };

  useEffect(function () {
    // 当从搜索切换回正常模式，并且没有搜索关键字的时候
    if (!searchVal && isSearch) {
      var dealData = getCheckStatus(menu['0'], selectVal.map(function (i) {
        return i.value;
      }));
      setMenu({
        '0': dealData.treeData
      });
    }
  }, [searchVal]);

  var emptyContainer = function emptyContainer() {
    return /*#__PURE__*/React.createElement("div", {
      className: "cascader-top-container",
      style: {
        width: '100%',
        padding: '10px 0'
      }
    }, /*#__PURE__*/React.createElement(Empty, _extends({
      image: Empty.PRESENTED_IMAGE_SIMPLE
    }, emptyProps)));
  };

  function onVisibleChange(visible) {
    if (disabled) return;
    setVisible(visible);
    onPopupVisibleChange(visible);
    if (!visible) setSearchVal('');
  }

  useEffect(function () {
    // 如果不可见就不要设置监听器
    if (!visible) return;

    function hidepopup(ev) {
      if (containerRef.current && !containerRef.current.contains(ev.target) && inputRef.current && !inputRef.current.contains(ev.target)) onVisibleChange(false);
    }

    document.body.addEventListener('click', hidepopup);
    return function () {
      document.body.removeEventListener('click', hidepopup);
    };
  }, [visible]);
  var containerRef = useRef();
  return /*#__PURE__*/React.createElement(Dropdown, {
    getPopupContainer: getPopupContainer,
    overlayClassName: "cascader-menu-w",
    trigger: ['click'],
    visible: visible,
    overlay: function overlay() {
      return (
        /*#__PURE__*/
        // rc-dropdonw@3.3.0会处理ref导致ref丢失
        React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
          ref: containerRef
        }, (menu['0'] && menu['0'].length ? searchVal ? renderSearch : renderDropdown : emptyContainer)()))
      );
    },
    animation: "slide-up"
  }, /*#__PURE__*/React.createElement("div", {
    onClick: function onClick() {
      return onVisibleChange(true);
    },
    style: style,
    className: "cs-cascader",
    ref: inputRef
  }, /*#__PURE__*/React.createElement(Input, {
    className: "cascader-input",
    placeholder: placeholder || '请选择',
    allowClear: true,
    value: searchVal,
    disabled: disabled || show // onChange={debounce(search, 700)(e)}
    ,
    onChange: search,
    prefix: /*#__PURE__*/React.createElement(CascaderTag$1, {
      disabled: disabled || show,
      selectVal: selectVal,
      onClose: function onClose() {
        return _onClose();
      }
    }),
    suffix: /*#__PURE__*/React.createElement(DownOutlined, {
      style: {
        color: 'rgba(0, 0, 0, 0.25)',
        width: '12px',
        height: '12px'
      }
    })
  })));
};

var multipleCascader = /*#__PURE__*/React.memo( /*#__PURE__*/forwardRef(MultipleCascader));

export default multipleCascader;
export { UseCascader as useCascader };
